import { HttpClient } from '@angular/common/http';
import { Injectable, Optional, SkipSelf } from '@angular/core';
import { EnvironmentService } from '@services/environment.service';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';

@Injectable({
  providedIn: 'root',
})
export class SecurityContextService {
  private globalState: any = {};
  public readonly loggedIn$ = new BehaviorSubject<boolean>(this.isLoggedIn());

  constructor(
    private environment: EnvironmentService,
    private http: HttpClient,
    private router: Router,
    @Optional() @SkipSelf() sharedService?: SecurityContextService,
  ) {
    if (sharedService) {
      throw new Error('SecurityContextService is already provided');
    }
  }

  authData = () => {
    return JSON.parse(localStorage.getItem('authDataQb') || '{}');
  };

  get = (key: string): any => {
    return localStorage[key];
  };

  set = (key: string, value: any) => {
    localStorage.setItem(key, JSON.stringify(value));
  };

  getUserName = () => {
    if (localStorage.getItem('authDataQb')) {
      return JSON.parse(localStorage.getItem('authDataQb') || '{}').username;
    }

    return '';
  };

  isLoggedIn() {
    if (localStorage.getItem('authDataQb')) {
      const loggedin = JSON.parse(
        localStorage.getItem('authDataQb') || '{}',
      ).loggedin;
      const token_expires = new Date(
        JSON.parse(localStorage.getItem('authDataQb') || '{}').token_expires,
      );

      if (!loggedin) {
        localStorage.removeItem('authDataQb');
        return false;
      }

      if (token_expires < new Date()) {
        console.log('Token expired - must login');
        localStorage.removeItem('authDataQb');
        return false;
      }

      return true;
    }

    return false;
  }

  logOut(): void {
    localStorage.removeItem('authDataQb');
    this.loggedIn$.next(false);
    this.router.navigate(['/login']);
  }

  login = async (username: string, password: string): Promise<any> => {
    const data =
      'grant_type=password&username=' +
      encodeURIComponent(username) +
      '&password=' +
      encodeURIComponent(password);

    const response = await lastValueFrom<any>(
      this.http.post(this.environment.config.apiBaseUrl + 'token', data, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }),
    );

    this.set('authDataQb', {
      token: response.access_token,
      token_issued: response['.issued'],
      token_expires: response['.expires'],
      token_expires_in: response.expires_in,
      username: username,
      loggedin: true,
      id: response.Id,
      accountid: parseInt(response.AccountID),
      accountuserid: parseInt(response.AccountUserID),
      firstname: response.Firstname,
      lastname: response.Lastname,
      franchisecode: response.FranchiseGroupCode,
      franchiseeid: response.FranchiseeID,
      accounttype: parseInt(response.AccountType),
    });
    this.loggedIn$.next(true);
    //Update Gainsight PX
    aptrinsic(
      'identify',
      {
        //User Fields
        id: response.Id, // Required for logged in app users
        UserID_lmn: response.Id,
        firstName: response.Firstname,
        lastName: response.Lastname,
        username: username,
        Username_lmn: username,
      },
      {
        id: response.AccountID, //Required
        AccountID_lmn: response.AccountID,
        FranchiseCode: response.FranchiseCode,
        FranchiseID: response.FranchiseeID,
      },
    );

    // Set datadog user info
    datadogRum.setUser({
      id: response.Id,
      account_id: response.AccountID,
      account_type: response.AccountType,
      account_user_id: response.AccountUserID,
      franchise_code: response.FranchiseGroupCode,
      name: response.Firstname + ' ' + response.Lastname,
    });

    return response;
  };
}
